<template>
  <Teleport to="body">
    <Transition name="editor-prof-fade">
      <div v-if="modelValue" class="editor-prof-mask" @click.self="onClose">
        <div class="editor-prof-wrap" @click.self="onClose">
          <Transition name="editor-prof-pop">
            <dialog
              v-if="modelValue"
              ref="dialogRef"
              class="editor-prof-panel"
              open
              aria-modal="true"
              :aria-labelledby="titleId"
            >
              <header class="editor-prof-head">
                <h2 :id="titleId" class="editor-prof-title">{{ copy.title }}</h2>
                <button type="button" class="editor-prof-close" :aria-label="copy.close" @click="onClose">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" />
                  </svg>
                </button>
              </header>

              <div class="editor-prof-body">
                <div class="editor-prof-avatar" :aria-label="copy.avatarAria(displayInitial, identityText)">
                  <img
                    v-if="avatarUrl"
                    class="editor-prof-avatar__img"
                    :src="avatarUrl"
                    :alt="identityText"
                    referrerpolicy="no-referrer"
                  />
                  <span v-else class="editor-prof-avatar__letter" aria-hidden="true">{{ displayInitial }}</span>
                </div>

                <dl class="editor-prof-fields">
                  <div class="editor-prof-field">
                    <dt>{{ identityLabel }}</dt>
                    <dd>{{ identityText }}</dd>
                  </div>
                  <div v-if="secondaryIdentityText" class="editor-prof-field">
                    <dt>{{ secondaryIdentityLabel }}</dt>
                    <dd>{{ secondaryIdentityText }}</dd>
                  </div>
                  <div class="editor-prof-field">
                    <dt>{{ copy.subscriptionLabel }}</dt>
                    <dd>
                      <template v-if="isSubscribed">
                        <div class="editor-prof-sub">
                          <div class="editor-prof-sub__top">
                            <span class="editor-prof-sub__status">{{ copy.subscribed }}</span>
                            <button type="button" class="editor-prof-details" @click="onOpenDetails">
                              {{ copy.details }}
                            </button>
                          </div>
                          <div class="editor-prof-sub__bottom">
                            <span class="editor-prof-sub__plan">
                              {{ tierLabel }}
                            </span>
                            <span class="editor-prof-muted editor-prof-sub__expires">
                              {{ tierExpiresText }}
                            </span>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <span class="editor-prof-muted">{{ copy.notSubscribed }}</span>
                        <button type="button" class="editor-prof-subscribe" @click="onSubscribeNow">
                          {{ copy.subscribeNow }}
                        </button>
                      </template>
                    </dd>
                  </div>
                  <div class="editor-prof-field">
                    <dt>{{ copy.createdLabel }}</dt>
                    <dd>{{ createdAtText }}</dd>
                  </div>
                </dl>
              </div>

              <footer class="editor-prof-foot">
                <button type="button" class="editor-prof-logout" @click="onLogout">
                  {{ copy.logout }}
                </button>
              </footer>
            </dialog>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>

  <EditorSubscriptionDetailDialog
    v-model="detailsOpen"
    :tier="membershipTier"
    :expires-at="membershipExpiresAt"
    :subscription-no="currentSubscriptionNo"
    :locale="props.locale"
    :marketing-origin="props.marketingOrigin"
    :token="session?.token || ''"
    @refresh-membership="refreshMembershipSnapshot"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from 'vue-i18n'

import { IMOCKUP_ANIMOCK_STATIC_URL, IMOCKUP_LOGIN_TEST } from '@/config/imockupBuildFlags'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { fetchWithBaUserToken } from '../../../../shared/utils/fetchWithBaUserToken'
import { resolveUserAvatarUrl } from '../../../../shared/utils/userAvatarUrl'
import EditorSubscriptionDetailDialog from './EditorSubscriptionDetailDialog.vue'

const props = defineProps<{
  modelValue: boolean
  locale: string
  marketingOrigin: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t, locale: i18nLocale } = useI18n()
const { session, userEmail, setSession, clearSession } = useEditorAuth()
const dialogRef = ref<HTMLDialogElement | null>(null)
const loggingOut = ref(false)
/** 标题元素 id，供 `aria-labelledby` 使用。 */
const titleId = 'editor-profile-title'

interface ProfileCopy {
  title: string
  close: string
  emailLabel: string
  nicknameLabel: string
  usernameLabel: string
  subscriptionLabel: string
  subscribed: string
  notSubscribed: string
  subscribeNow: string
  details: string
  createdLabel: string
  createdUnknown: string
  logout: string
  avatarAria: (initial: string, email: string) => string
}

const copy = computed<ProfileCopy>(() => ({
  title: t('auth.profile.title'),
  close: t('auth.profile.close'),
  emailLabel: t('auth.profile.emailLabel'),
  nicknameLabel: t('auth.profile.nicknameLabel', 'Nickname'),
  usernameLabel: t('auth.profile.usernameLabel', 'Username'),
  subscriptionLabel: t('auth.profile.subscriptionLabel'),
  subscribed: t('auth.profile.subscribed'),
  notSubscribed: t('auth.profile.notSubscribed'),
  subscribeNow: t('auth.profile.subscribeNow'),
  details: t('auth.profile.details'),
  createdLabel: t('auth.profile.createdLabel'),
  createdUnknown: t('auth.profile.createdUnknown'),
  logout: t('auth.profile.logout'),
  avatarAria: (initial, email) => t('auth.profile.avatarAria', { initial, email }),
}))

const isSubscribed = computed(() => Boolean(session.value?.isSubscribed))
const membershipTier = computed(
  () => (session.value?.membershipTier || 'none') as 'none' | 'monthly' | 'yearly' | 'lifetime',
)
const membershipExpiresAt = computed(() => Number(session.value?.membershipExpiresAt || 0))
const currentSubscriptionNo = computed(() => String(session.value?.currentSubscriptionNo || ''))
const detailsOpen = ref(false)
const nicknameText = computed(() => String(session.value?.nickname || '').trim())
const usernameText = computed(() => String(session.value?.username || '').trim())
const emailText = computed(() => String(userEmail.value || '').trim())
const avatarUrl = computed(() => {
  if (!session.value) return ''
  return resolveUserAvatarUrl(session.value.avatar, IMOCKUP_ANIMOCK_STATIC_URL)
})

const identityLabel = computed(() => {
  if (nicknameText.value) return copy.value.nicknameLabel
  if (usernameText.value) return copy.value.usernameLabel
  return copy.value.emailLabel
})

const identityText = computed(() => nicknameText.value || usernameText.value || emailText.value || 'Animock User')

const secondaryIdentityLabel = computed(() => {
  if (nicknameText.value && usernameText.value) return copy.value.usernameLabel
  if ((nicknameText.value || usernameText.value) && emailText.value) return copy.value.emailLabel
  return ''
})

const secondaryIdentityText = computed(() => {
  if (nicknameText.value && usernameText.value) return usernameText.value
  if ((nicknameText.value || usernameText.value) && emailText.value) return emailText.value
  return ''
})

const tierLabel = computed(() => {
  switch (membershipTier.value) {
    case 'monthly':
      return t('auth.profile.tier.monthly')
    case 'yearly':
      return t('auth.profile.tier.yearly')
    case 'lifetime':
      return t('auth.profile.tier.lifetime')
    default:
      return t('auth.profile.tier.none')
  }
})

const tierExpiresText = computed(() => {
  if (!isSubscribed.value) return ''
  if (membershipTier.value === 'lifetime' || membershipExpiresAt.value === 0) {
    return t('auth.profile.permanent')
  }
  const ts = membershipExpiresAt.value
  if (!ts) return '—'
  try {
    const d = new Date(ts * 1000)
    if (Number.isNaN(d.getTime())) return '—'
    const date = new Intl.DateTimeFormat(i18nLocale.value, { dateStyle: 'medium' }).format(d)
    return t('auth.profile.expiresAt', { date })
  } catch {
    return '—'
  }
})

/**
 * 取邮箱用于头像展示的首字符（大写）。
 *
 * @param email - 用户邮箱
 */
function displayInitialFrom(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return 'A'
  return trimmed[0]!.toUpperCase()
}

const displayInitial = computed(() => displayInitialFrom(identityText.value))

const createdAtText = computed(() => {
  const raw = session.value?.createdAt
  if (!raw) return copy.value.createdUnknown
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return copy.value.createdUnknown
    return new Intl.DateTimeFormat(i18nLocale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
  } catch {
    return copy.value.createdUnknown
  }
})

watch(
  () => props.modelValue,
  (opened) => {
    if (import.meta.env.SSR) return
    if (opened) {
      void refreshMembershipSnapshot()
      nextTick(() => {
        dialogRef.value?.querySelector<HTMLElement>('.editor-prof-close')?.focus()
      })
    }
  },
)

/**
 * 打开个人信息弹窗时刷新会员快照（与营销站保持同步）。
 */
async function refreshMembershipSnapshot(): Promise<void> {
  const token = session.value?.token
  if (!token) return
  try {
    const origin = props.marketingOrigin.replace(/\/$/, '')
    const res = await fetchWithBaUserToken(
      `${origin}/api/entitlement/current`,
      { method: 'GET', credentials: 'include' },
      token,
    )
    const raw = (await res.json().catch(() => ({}))) as unknown
    const envelope =
      raw && typeof raw === 'object' && raw !== null && 'data' in raw && ('code' in raw || 'msg' in raw)
        ? (raw as { code?: number; data?: unknown })
        : null
    // 鉴权失败 / 后端瞬时不可用：保留现有 session，避免把订阅快照覆盖为 'none'/'' 的脏值；
    // 详情弹窗在这种情况下走 `subQueryFailed` 占位渲染分支，仍可让用户重新查询。
    if (envelope && Number(envelope.code ?? 0) !== 1) return
    const dto = envelope ? envelope.data : raw
    const typed = dto as {
      tier?: 'none' | 'monthly' | 'yearly' | 'lifetime'
      is_active?: boolean
      expires_at?: number
      subscription_no?: string
    }
    const planCode =
      typed?.tier === 'yearly'
        ? 'yearly'
        : typed?.tier === 'lifetime'
          ? 'lifetime'
          : typed?.tier === 'monthly'
            ? 'monthly'
            : ''
    const isActive = Boolean(typed?.is_active)
    // `subscription_no` 始终从 dto 派生：dto 缺省或空字符串时显式重置为 ''，避免上一次订阅号泄漏到下一会员周期。
    const subscriptionNo = String(typed?.subscription_no || '').trim()
    const next = {
      ...(session.value || { token, email: userEmail.value }),
      isSubscribed: isActive,
      membershipTier: (typed?.tier || 'none') as any,
      membershipPlanCode: planCode,
      membershipStartedAt: 0,
      membershipExpiresAt: Number(typed?.expires_at || 0),
      currentSubscriptionNo: subscriptionNo,
    }
    setSession(next)
  } catch {
    // 网络异常等：保留现有 session，由详情弹窗的占位/重试分支兜底
  }
}

function onOpenDetails(): void {
  detailsOpen.value = true
}

onMounted(() => {
  globalThis.addEventListener('keydown', onEscClose)
})

onBeforeUnmount(() => {
  globalThis.removeEventListener('keydown', onEscClose)
})

/**
 * 关闭弹窗。
 */
function onClose(): void {
  emit('update:modelValue', false)
}

/**
 * `Escape` 关闭弹窗。
 *
 * @param ev - 键盘事件
 */
function onEscClose(ev: KeyboardEvent): void {
  if (!props.modelValue || ev.key !== 'Escape') return
  emit('update:modelValue', false)
}

/**
 * 跳转到营销站订阅页。
 */
function onSubscribeNow(): void {
  const normalizedOrigin = props.marketingOrigin.replace(/\/$/, '')
  globalThis.location.assign(`${normalizedOrigin}${resolvePricingPathByLocale(props.locale)}`)
}

/**
 * 按营销站 i18n 路由策略计算订阅页路径（`prefix_except_default`）。
 *
 * @param locale - 当前编辑器语种
 */
function resolvePricingPathByLocale(locale: string): string {
  const normalized = locale.trim().toLowerCase()
  if (normalized.startsWith('zh')) return '/zh-CN/pricing'
  if (normalized.startsWith('ja')) return '/ja/pricing'
  return '/pricing'
}

/**
 * 推导认证 API 根地址：开发期 5173 → 3000，生产同 origin。
 */
function resolveAuthApiBase(): string {
  if (globalThis.window === undefined) return ''
  const current = globalThis.location
  if (current.port === '5173') {
    return `${current.protocol}//${current.hostname}:3000`
  }
  return `${current.protocol}//${current.host}`
}

/**
 * 退出登录：调用后端注销（测试模式跳过），并清空本地会话。
 */
async function onLogout(): Promise<void> {
  if (loggingOut.value) return
  loggingOut.value = true
  try {
    if (!IMOCKUP_LOGIN_TEST && session.value?.token) {
      await fetchWithBaUserToken(
        `${resolveAuthApiBase()}/api/auth/logout`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            token: session.value.token,
            refreshToken: session.value.refreshToken || '',
          }),
        },
        session.value.token,
      ).catch(() => undefined)
    }
  } finally {
    clearSession()
    loggingOut.value = false
    emit('update:modelValue', false)
  }
}
</script>

<style scoped>
.editor-prof-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgb(2 6 23 / 66%);
  padding: 1rem;
}

.editor-prof-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.editor-prof-panel {
  position: relative;
  inset: auto;
  left: auto;
  right: auto;
  width: min(400px, calc(100vw - 2rem));
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  margin: 0 auto;
  padding: 1rem 1rem 0.85rem;
  border-radius: 14px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: var(--im-shadow);
}

.editor-prof-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.editor-prof-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--im-text);
}

.editor-prof-close {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.editor-prof-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-prof-body {
  margin-top: 1rem;
  display: grid;
  justify-items: center;
  gap: 1rem;
}

.editor-prof-avatar {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #156f97, #1f7ab8);
  color: #fff;
  font-size: 1.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 10px 28px rgb(21 111 151 / 28%);
  overflow: hidden;
}

.editor-prof-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.editor-prof-fields {
  margin: 0;
  width: 100%;
  display: grid;
  gap: 0.85rem;
}

.editor-prof-field {
  margin: 0;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
}

.editor-prof-field dt {
  margin: 0 0 0.25rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--im-text-secondary);
}

.editor-prof-field dd {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--im-text);
  overflow-wrap: anywhere;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.65rem;
}

.editor-prof-muted {
  color: var(--im-text-secondary);
  font-weight: 600;
}

.editor-prof-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.75rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
}

.editor-prof-badge--ok {
  background: color-mix(in srgb, #10b981 14%, var(--im-surface));
  color: #047857;
  border: 1px solid color-mix(in srgb, #10b981 35%, transparent);
}

.editor-prof-sub {
  display: grid;
  gap: 0.35rem;
  min-width: 0;
}

.editor-prof-sub__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.editor-prof-sub__status {
  display: inline-flex;
  align-items: center;
  padding: 0 0.55rem;
  min-height: 28px;
  border-radius: 999px;
  background: color-mix(in srgb, #10b981 12%, var(--im-surface));
  border: 1px solid color-mix(in srgb, #10b981 32%, transparent);
  color: #047857;
  font-weight: 900;
  font-size: 0.8rem;
  white-space: nowrap;
}

.editor-prof-sub__bottom {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
  align-items: center;
}

.editor-prof-sub__plan {
  font-weight: 900;
  color: var(--im-text);
}

.editor-prof-sub__expires {
  font-weight: 750;
}

.editor-prof-details {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--im-muted);
  font-weight: 800;
  font-size: 0.86rem;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
}

.editor-prof-details:hover {
  color: var(--im-text);
}

.editor-prof-details:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-prof-subscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 0.85rem;
  border-radius: 10px;
  border: 0;
  background: linear-gradient(135deg, #156f97, #1f7ab8);
  color: #fff;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
}

.editor-prof-subscribe:hover {
  filter: brightness(1.03);
}

.editor-prof-subscribe:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-prof-foot {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--im-border);
}

.editor-prof-logout {
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: transparent;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
}

.editor-prof-logout:hover {
  background: rgb(185 28 28 / 8%);
}

.editor-prof-logout:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-prof-fade-enter-active,
.editor-prof-fade-leave-active {
  transition: opacity 220ms ease;
}

.editor-prof-fade-enter-from,
.editor-prof-fade-leave-to {
  opacity: 0;
}

.editor-prof-pop-enter-active,
.editor-prof-pop-leave-active {
  transition:
    opacity 260ms ease,
    transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.editor-prof-pop-enter-from,
.editor-prof-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .editor-prof-fade-enter-active,
  .editor-prof-fade-leave-active,
  .editor-prof-pop-enter-active,
  .editor-prof-pop-leave-active {
    transition: none;
  }
}
</style>
